import React, { createContext, useContext, useEffect, useState } from "react";
import { useAddSelection, useClearMySelectionCount, useRemoveSelection } from "./apiHooks";
import MaxMySelectionCountModal from "./Modals/MaxMySelectionCountModal";
import { FilterContext } from "components/filters/FilterContext";

export interface MySelectionOptions<TItem> {
  mainArea: server.dto.MainAreas
  isSelected: (item: TItem) => boolean
  batchActions: MySelectionBatchAction[]
}

export interface MySelectionBatchAction {
  label: string
  modal: React.FC<{ onClose: () => void, mainArea: server.dto.MainAreas }>
  props?: any
}

interface MySelectionProviderProps<TItem> {
  options: MySelectionOptions<TItem> | undefined
  initialSelectionCount: number
  items: TItem[]
  getId: (item: TItem) => number
  refetch: () => void
}

interface MySelectionContextValue<TItem> {
  enabled: boolean
  selectionCount: number
  selectedOnThisPage: number
  showOnlyMySelection: boolean
  batchActions: MySelectionBatchAction[]
  isSelected: (item: TItem) => boolean
  toggleAll: () => void
  toggleItem: (item: TItem) => void
  toggleShowOnlyMySelection: () => void
  toggleBatchAction: (index: number) => void
  clearSelection: () => void
}

const defaultMySelectionContext = {
  enabled: false,
  selectionCount: 0,
  selectedOnThisPage: 0,
  showOnlyMySelection: false,
  batchActions: [],
  isSelected: () => false,
  toggleAll: () => { },
  toggleItem: () => { },
  toggleShowOnlyMySelection: () => { },
  toggleBatchAction: () => { },
  clearSelection: () => { }
};

export const MySelectionContext = createContext<MySelectionContextValue<any>>(defaultMySelectionContext);

export const MySelectionProvider = <TItem extends any>(props: React.PropsWithChildren<MySelectionProviderProps<TItem>>) => {
  const { options, initialSelectionCount, items, getId, refetch } = props;

  if (options === undefined) {
    return <MySelectionContext.Provider value={defaultMySelectionContext}>{props.children}</MySelectionContext.Provider>;
  }

  const filterContext = useContext(FilterContext);
  const showOnlyMySelection = filterContext.getValue("onlyMySelection").showOnlyMySelection;

  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [selectedOnOtherPages, setSelectedOnOtherPages] = useState<number>(0);
  const checkedIdsSet = new Set(checkedIds);
  const totalSelected = checkedIds.length + selectedOnOtherPages;
  const [openBatchSelectionModalIndex, setOpenBatchSelectionModalIndex] = useState<number | null>(null);
  const [showingMaxSelectionCountModal, setShowingMaxSelectionCountModal] = useState<boolean>(false);

  const removeSelectionApi = useRemoveSelection(options.mainArea);
  const addSelectionApi = useAddSelection(options.mainArea);
  const clearCountApi = useClearMySelectionCount();

  const toggleItem = async (item: TItem) => {
    const itemId = getId(item);
    let newList: number[] = [];
    if (checkedIdsSet.has(itemId)) {
      newList = checkedIds.filter(id => id !== itemId);
      await removeSelectionApi.mutateAsync([itemId]);
    } else {
      newList = [...checkedIds, itemId];
      if (totalSelected >= serverReference.constants.mySelection.maxItemsInMySelection) {
        setShowingMaxSelectionCountModal(true);
        return;
      }
      await addSelectionApi.mutateAsync([itemId]);
    }

    if (checkedIdsSet.has(itemId) && showOnlyMySelection) {
      refetch();
    }

    setCheckedIds(newList);
    if (newList.length === 0 && showOnlyMySelection) {
      toggleShowOnlyMySelection();
    }
  };

  const isToggleAllButonChecked = checkedIds.length > 0;

  const toggleAll = () => {
    // Unchecking all
    if (isToggleAllButonChecked) {
      setCheckedIds([]);
      removeSelectionApi.mutate(checkedIds);

      // Also remove the "only my selection" filter if it's active
      if (showOnlyMySelection) {
        filterContext.setValue("onlyMySelection", { showOnlyMySelection: false });
      }
    } else { // Checking all
      if (totalSelected + items.length > serverReference.constants.mySelection.maxItemsInMySelection) {
        setShowingMaxSelectionCountModal(true);
        return;
      }
      const ids = items.map(item => getId(item));
      setCheckedIds(ids);
      addSelectionApi.mutate(ids);
    }
  };

  const toggleShowOnlyMySelection = () => {
    filterContext.setValue("onlyMySelection", { showOnlyMySelection: !filterContext.getValue("onlyMySelection").showOnlyMySelection });
  };

  const clearSelection = async () => {
    setCheckedIds([]);
    await clearCountApi.mutateAsync(options.mainArea);
    toggleShowOnlyMySelection();
  };

  const toggleBatchAction = (index: number) => {
    setOpenBatchSelectionModalIndex(index);
  };

  const renderOpenBatchActionModal = () => {
    if (openBatchSelectionModalIndex === null) return null;
    const Modal = options.batchActions[openBatchSelectionModalIndex].modal;
    return <Modal
      {...options.batchActions[openBatchSelectionModalIndex].props}
      mainArea={options.mainArea}
      onClose={() => {
        setOpenBatchSelectionModalIndex(null);
        refetch();
      }}
    />;
  };

  useEffect(() => {
    if (items === null || items.length === 0) return;

    const ids = items.filter(options.isSelected).map(item => getId(item));
    setCheckedIds(ids);
    setSelectedOnOtherPages(initialSelectionCount - ids.length);
  }, [items]);

  return (
    <MySelectionContext.Provider value={{
      enabled: true,
      selectionCount: totalSelected,
      selectedOnThisPage: checkedIds.length,
      showOnlyMySelection,
      batchActions: options.batchActions,
      isSelected: (item: TItem) => checkedIdsSet.has(getId(item)),
      toggleAll,
      toggleItem,
      toggleShowOnlyMySelection,
      toggleBatchAction,
      clearSelection
    }}>
      {props.children}
      {renderOpenBatchActionModal()}
      <MaxMySelectionCountModal
        isOpen={showingMaxSelectionCountModal}
        onClose={() => { setShowingMaxSelectionCountModal(false); }}
      />
    </MySelectionContext.Provider>
  );
};